import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Flame, Droplets, Beef, Wheat, Loader2, TrendingUp } from 'lucide-react';
import { getNutritionPeriodSummary } from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { PeriodPicker } from './PeriodPicker';

/**
 * Trend section for Nutrition page — shows daily averages over a chosen period.
 * Independent of the day-focused CalendarStrip view above it.
 */
export function NutritionTrendSection() {
    const { language } = useLanguage();
    const [days, setDays] = useState(7);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getNutritionPeriodSummary(days)
            .then(res => { if (!cancelled) setData(res.data); })
            .catch(err => { if (!cancelled) console.error('period summary failed:', err); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [days]);

    const avg = data?.averages_per_day || {};
    const goals = data?.goals || {};
    const tiles = [
        {
            icon: Flame,
            label: language === 'de' ? 'Ø kcal/Tag' : 'Ø kcal/day',
            value: avg.calories || 0,
            goal: goals.calorie_goal,
            color: 'text-orange-500',
        },
        {
            icon: Beef,
            label: language === 'de' ? 'Ø Protein' : 'Ø Protein',
            value: `${avg.protein || 0}g`,
            goal: goals.protein_goal ? `${goals.protein_goal}g` : null,
            color: 'text-red-500',
        },
        {
            icon: Wheat,
            label: language === 'de' ? 'Ø Carbs' : 'Ø Carbs',
            value: `${avg.carbs || 0}g`,
            goal: goals.carbs_goal ? `${goals.carbs_goal}g` : null,
            color: 'text-amber-500',
        },
        {
            icon: Droplets,
            label: language === 'de' ? 'Ø Wasser' : 'Ø Water',
            value: `${((avg.water_ml || 0) / 1000).toFixed(1)}L`,
            goal: goals.water_goal_ml ? `${(goals.water_goal_ml / 1000).toFixed(1)}L` : null,
            color: 'text-cyan-500',
        },
    ];

    return (
        <Card data-testid="nutrition-trend-section">
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    {language === 'de' ? 'Trend' : 'Trend'}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
                <PeriodPicker value={days} onChange={setDays} testid="nutrition-period" />

                {loading ? (
                    <div className="py-6 flex items-center justify-center text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" />
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-2">
                            {tiles.map((t, i) => {
                                const Icon = t.icon;
                                return (
                                    <div
                                        key={i}
                                        className="rounded-xl bg-muted/40 p-3 flex items-center gap-3"
                                        data-testid={`nutrition-trend-tile-${i}`}
                                    >
                                        <Icon className={`w-5 h-5 ${t.color}`} />
                                        <div className="min-w-0">
                                            <p className="text-[11px] text-muted-foreground truncate">{t.label}</p>
                                            <p className="text-sm font-bold leading-tight">{t.value}</p>
                                            {t.goal && (
                                                <p className="text-[10px] text-muted-foreground/70">
                                                    {language === 'de' ? 'Ziel' : 'Goal'}: {t.goal}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {data && (
                            <p className="text-[11px] text-muted-foreground text-center pt-1">
                                {language === 'de'
                                    ? `${data.active_days_any} von ${data.period_days} Tagen aktiv`
                                    : `${data.active_days_any} of ${data.period_days} days active`}
                            </p>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}
